import Typography from '@mui/material/Typography';
import React from 'react';
import { Row, Button, OverlayTrigger, Tooltip  } from 'react-bootstrap';
import CamAppIcon from 'components/icons/CamAppIcon';
import toast from 'react-hot-toast'
import { CollegeInformationDetail } from './CounselorCollegeSearch';
import collegeServices from 'services/collegeServices';
import { checkEnableParseJSON } from 'components/helper/helper';
import { Box } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useAppSelector } from 'app/hooks';
import LoadingContent from 'components/LoadingContent';
import SavingIndicator from 'screen/CollegeManagement/SavingIndicator';
import { LikelihoodRanges } from 'constants/likelihoodBalance';
import { normalizeLikelihoodRanges } from 'constants/likelihoodBalance';
import { getCamBaseUrl, isGradMapSessionValid, notifyCamSessionExpired, openInCam } from 'utils/camAuth';
import { getAccessToken } from 'utils/sessionStorage';

type Props = {};

const CounselorCollegeView = (props: Props) => {
  const params: { id?: string | number } = useParams();

  const [myCurrentList, setMyCurrentList] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  // eslint-disable-next-line eqeqeq
  const menu = useAppSelector((store:any) => store.counselor.studentList);
  const currentStudentItem = menu.find((e: any) => e.student_id?.toString() === params?.id);
  const student = currentStudentItem?.student_info;
  const studentName = student?.first_name;

  React.useEffect(() => {
    if (!params?.id) return;
    setLoading(true);
    collegeServices
      .getMyCollege(params.id)
      .then((res: any) => {
        if (!!res?.colleges_array) {
          const colleges_array = checkEnableParseJSON(res?.colleges_array, []);
          const likedColleges: any = [];
          colleges_array.forEach((element: any) => {
            (element?.collegeList ?? []).forEach((college: any) => {
              college.is_saved && likedColleges.push(college);
            });
          });
          setMyCurrentList(likedColleges);
        }
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.id]);

  if (loading) {
    return (
      <>
        <SavingIndicator loading loadingMessage="Loading saved colleges..." />
        <LoadingContent />
      </>
    );
  }
  
  const handleOpenInCam = async (miniItem:any) => {
    if (!getAccessToken()) {
      notifyCamSessionExpired();
      return;
    }

    // Token is present but may already be expired/revoked server-side —
    // confirm with the backend before handing it off to CAM.
    const sessionValid = await isGradMapSessionValid();
    if (!sessionValid) {
      notifyCamSessionExpired();
      return;
    }

    if (!getCamBaseUrl()) {
      toast.error('CAM is not configured. Please contact support.');
      return;
    }

    const studentIdNow = currentStudentItem?.student_id ?? Number(params?.id);
    if (!studentIdNow) return;

    let camOpenContext: {
      requirementsReturnTo: string | null;
      likelihoodRanges: LikelihoodRanges;
    } | null = null;

    try {
      let ctx1 = await collegeServices.getCamOpenContext(studentIdNow);
      camOpenContext = {
        requirementsReturnTo: ctx1.requirementsReturnTo,
        likelihoodRanges: normalizeLikelihoodRanges(ctx1.likelihoodRanges),
      };
    } catch {
      camOpenContext = null;
    }

    try {
      let legacyId = miniItem?.college_id;
      const ctx = await collegeServices.getCamOpenContext(
        studentIdNow,
        legacyId != null && !Number.isNaN(legacyId) ? legacyId : undefined,
      );
      const returnTo = ctx.requirementsReturnTo ?? camOpenContext?.requirementsReturnTo ?? '/';
      if (!openInCam(returnTo)) {
        toast.error('Could not open CAM. Please allow pop-ups for this site.');
      }
    } catch {
      const fallback = camOpenContext?.requirementsReturnTo ?? '/';
      if (!openInCam(fallback)) {
        toast.error('Could not open CAM. Please allow pop-ups for this site.');
      }
    }
  };

  return (
    <Row className="px-2">
      <Typography
        sx={{ width: '100%', textAlign: 'center', fontWeight: 'bold', fontSize: '1.5rem' }}
      >
        Colleges {studentName} is Applying To
      </Typography>
      <Box sx={{ padding: '0 84px !important' }}>
        {myCurrentList.length === 0 ? (
          <Typography sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
            No saved colleges yet.
          </Typography>
        ) : (
          myCurrentList.map((miniItem, index) => (
            <div key={index}>
              <CollegeInformationDetail
                miniItem={miniItem}
                index={index}
                item={myCurrentList}
                studentName={studentName}
                camButton={(
                  <OverlayTrigger
                    placement={'right'}
                    overlay={
                      <Tooltip id={`tooltip-cam`}>
                        <strong>CAM</strong>
                      </Tooltip>
                    }
                  >
                    <Button
                      className="border-top rounded-0 text-field-light"
                      variant="outline"
                      onClick={ async() => {await handleOpenInCam(miniItem)}}
                    >
                      <CamAppIcon />
                    </Button>
                  </OverlayTrigger>
                )}            
              />
            </div>
          ))
        )}
      </Box>
    </Row>
  );
};

export default CounselorCollegeView;
